import {
  createTheme,
  PaletteColor,
  PaletteColorOptions,
} from '@mui/material/styles';
import { RFValue } from './utils/Utils';

declare module '@mui/material/styles' {
  interface TypographyVariants {
    superTitle?: React.CSSProperties;
    title1?: React.CSSProperties;
    title1Thin?: React.CSSProperties;
    title2?: React.CSSProperties;
    title2Bold: React.CSSProperties;
    title3: React.CSSProperties;
    title3Bold: React.CSSProperties;
    title4: React.CSSProperties;
    title4Regular: React.CSSProperties;
    title5: React.CSSProperties;
    body1?: React.CSSProperties;
    body1Bold: React.CSSProperties;
    body2?: React.CSSProperties;
    body2UnderLine: React.CSSProperties;
    body2Bold: React.CSSProperties;
    body3?: React.CSSProperties;
    body3UnderLine: React.CSSProperties;
    body3Bold: React.CSSProperties;
    small?: React.CSSProperties;
    buttonText: React.CSSProperties;
    buttonTextUnderLine: React.CSSProperties;
    linkButton: React.CSSProperties;
    linkButtonUnderline: React.CSSProperties;
    textAv1: React.CSSProperties;
    textAv2: React.CSSProperties;
    textAv3: React.CSSProperties;
  }

  interface TypographyVariantsOptions {
    superTitle?: React.CSSProperties;
    title1?: React.CSSProperties;
    title1Thin?: React.CSSProperties;
    title2?: React.CSSProperties;
    title2Bold?: React.CSSProperties;
    title3?: React.CSSProperties;
    title3Bold?: React.CSSProperties;
    title4?: React.CSSProperties;
    title4Regular?: React.CSSProperties;
    title5?: React.CSSProperties;
    body1?: React.CSSProperties;
    body1Bold?: React.CSSProperties;
    body2?: React.CSSProperties;
    body2UnderLine?: React.CSSProperties;
    body2Bold?: React.CSSProperties;
    body3?: React.CSSProperties;
    body3UnderLine?: React.CSSProperties;
    body3Bold?: React.CSSProperties;
    small?: React.CSSProperties;
    buttonText?: React.CSSProperties;
    buttonTextUnderLine?: React.CSSProperties;
    linkButton?: React.CSSProperties;
    linkButtonUnderline?: React.CSSProperties;
    textAv1?: React.CSSProperties;
    textAv2?: React.CSSProperties;
    textAv3?: React.CSSProperties;
  }
}

declare module '@mui/material/styles' {
  interface Palette {
    input: PaletteColor;
  }
  interface PaletteOptions {
    input?: PaletteColorOptions;
  }
}

declare module '@mui/material/TextField' {
  interface TextFieldPropsColorOverrides {
    input: true;
  }
}

const theme = createTheme({
  palette: {
    primary: { main: '#F29F05' },
    secondary: { main: '#9ABF80' },
    input: { main: '#298140' }, // Added custom2 color
    error: { main: '#F23A29' },
    background: { default: '#298140', paper: '#EDEAE7' },
    text: { primary: '#49454F', secondary: '#8A8681' },
    action: {
      disabled: '#8A8681',
      hover: '#EDEAE7',
      selected: '#EDEAE7',
    },
    divider: '#EDEAE7',
    success: { main: '#298140' },
  },
  components: {
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#F29F05',
          },
        },
      },
    },
    MuiFilledInput: {
      styleOverrides: {
        root: {
          '&.Mui-focused': {
            backgroundColor: '#E8F5E9', // Optional: Add focus background color for filled variant
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiInputBase-input': {
            fontSize: '16px', // Ensure font size is at least 16px to prevent zooming
          },
        },
      },
    },
  },
  typography: {
    fontFamily: "'Nunito', 'Arial', sans-serif",
    allVariants: {
      color: '#000000',
    },
    superTitle: {
      fontFamily: 'Nunito-Bold',
      fontSize: RFValue(48.8),
      fontWeight: 'bold',
    },
    title1: {
      fontFamily: 'Nunito-Bold',
      fontSize: RFValue(39.1),
      fontWeight: 'bold',
    },
    title1Thin: {
      fontFamily: 'Nunito-Regular',
      fontSize: RFValue(36),
      fontWeight: 'regular',
    },
    title2: {
      fontFamily: 'Nunito-Medium',
      fontSize: RFValue(31.25),
      fontWeight: 'medium',
    },
    title2Bold: {
      fontFamily: 'Nunito-Bold',
      fontSize: RFValue(31.25),
      fontWeight: 'bold',
    },
    title3: {
      fontFamily: 'Nunito-Medium',
      fontSize: RFValue(25),
      fontWeight: 'medium',
    },
    title3Bold: {
      fontFamily: 'Nunito-Bold',
      fontSize: RFValue(25),
      fontWeight: 'bold',
    },
    title4: {
      fontFamily: 'Nunito-Bold',
      fontSize: RFValue(20),
      fontWeight: 'bold',
    },
    title4Regular: {
      fontFamily: 'Nunito-Regular',
      fontSize: RFValue(20),
      fontWeight: 'regular',
    },
    title5: {
      fontFamily: 'Nunito-Bold',
      fontSize: RFValue(16),
      fontWeight: 'bold',
    },
    body1: {
      fontFamily: 'Nunito-Medium',
      fontSize: RFValue(16),
      fontWeight: 'medium',
    },
    body1Bold: {
      fontFamily: 'Nunito-Bold',
      fontSize: RFValue(16),
      fontWeight: 'bold',
    },
    body2: {
      fontFamily: 'Nunito-Regular',
      fontSize: RFValue(14),
      fontWeight: 'regular',
    },
    body2UnderLine: {
      fontFamily: 'Nunito-Regular',
      fontSize: RFValue(14),
      fontWeight: 'regular',
      textDecorationLine: 'underline',
    },
    body2Bold: {
      fontFamily: 'Nunito-Bold',
      fontSize: RFValue(14),
      fontWeight: 'bold',
    },
    body3: {
      fontFamily: 'Nunito-Regular',
      fontSize: RFValue(12),
      fontWeight: 'regular',
    },
    body3UnderLine: {
      fontFamily: 'Nunito-Regular',
      fontSize: RFValue(12),
      fontWeight: 'regular',
      textDecorationLine: 'underline',
    },
    body3Bold: {
      fontFamily: 'Nunito-Bold',
      fontSize: RFValue(12),
      fontWeight: 'bold',
    },
    small: {
      fontFamily: 'Nunito-Bold',
      fontSize: RFValue(12.8),
      fontWeight: 'bold',
    },
    buttonText: {
      fontFamily: 'Nunito-Bold',
      fontSize: RFValue(16),
      fontWeight: 'bold',
    },
    buttonTextUnderLine: {
      fontFamily: 'Nunito-Bold',
      fontSize: RFValue(16),
      fontWeight: 'bold',
      textDecorationLine: 'underline',
    },
    linkButton: {
      fontFamily: 'Nunito-Bold',
      fontSize: RFValue(16),
      fontWeight: 'bold',
    },
    linkButtonUnderline: {
      fontFamily: 'Nunito-Bold',
      fontSize: RFValue(16),
      fontWeight: 'bold',
      textDecorationLine: 'underline',
    },
    textAv1: {
      fontFamily: 'Alkalami-Regular',
      fontSize: RFValue(28),
      fontWeight: 'bold',
    },
    textAv2: {
      fontFamily: 'Alkalami-Regular',
      fontSize: RFValue(90),
      fontWeight: 'regular',
    },
    textAv3: {
      fontFamily: 'Alkalami-Regular',
      fontSize: RFValue(140),
      fontWeight: 'regular',
    },
  },
});

export default theme;
